import Modal from "common/Modal/Modal";
import React from "react";
import Text from "common/Typography/Text";
import { getUsageTitleKey } from "@shared/features/settings/utils";
import Flex from "common/Boxes/Flex";
import CurrentUsageValue from "features/settings/Quota/CurrentUsageValue";
import styled, { css, useTheme } from "styled-components";
import { USAGE_TYPE_TO_LUCIDE_ICON } from "features/settings/NewLook/usageIcons.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { USAGE_TYPE_TO_ICON } from "features/settings/common/constants";
import { typography } from "@shared/common/Themes/typography";
import { DowngradeQuotaModalProps } from "@shared/features/settings/subscription/types";
import useDowngradeQuotaModal from "@shared/features/settings/subscription/hooks/useDowngradeQuotaModal";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const DowngradeQuotaModal = ({
  isOpen,
  onCancel,
  selectedPlanUsageLimits,
  currentQuotaUsage,
  planType,
}: DowngradeQuotaModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isNewLook } = useTheme();
  const { getOverLimitUsageTypes, modalData } = useDowngradeQuotaModal({
    selectedPlanUsageLimits,
    currentQuotaUsage,
    planType,
  });

  const getOverLimitUsages = () => {
    const overLimitUsageTypes = getOverLimitUsageTypes();

    return (
      <UsagesWrapper>
        {overLimitUsageTypes.map((usageType) => {
          const translationKey = getUsageTitleKey(usageType, planType);
          const IconName = USAGE_TYPE_TO_ICON[usageType];
          const NewLookIcon = USAGE_TYPE_TO_LUCIDE_ICON[usageType];
          let icon = IconName ? (
            <IconName style={{ marginRight: 8 }} />
          ) : undefined;
          if (isNewLook && NewLookIcon) {
            icon = (
              <NewLookIcon
                size={16}
                strokeWidth={NEW_LOOK_ICON_STROKE}
                style={{ marginRight: 10, flex: "none" }}
              />
            );
          }
          return (
            <UsageRow key={usageType}>
              <Flex>
                {icon}
                {translationKey && t(translationKey)}
              </Flex>
              <CurrentUsageValue
                usageType={usageType}
                currentUsage={currentQuotaUsage?.[usageType]}
                usageLimit={selectedPlanUsageLimits?.[usageType]}
              />
            </UsageRow>
          );
        })}
      </UsagesWrapper>
    );
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      onOk={onCancel}
      title={modalData.title}
      width={520}
    >
      <Text
        style={{
          fontWeight: isNewLook
            ? typography.fontWeights.NORMAL
            : typography.fontWeights.MEDIUM,
          marginBottom: 4,
        }}
      >
        {modalData.content[0]}
      </Text>
      <Text type="secondary">{modalData.content[1]}</Text>
      {getOverLimitUsages()}
    </Modal>
  );
};

export default DowngradeQuotaModal;

const UsagesWrapper = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  margin-top: 8px;
`;
const UsageRow = styled.div`
  padding: 14px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.light};
  }
  &:last-of-type {
    padding-bottom: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 12px 0;
      &:not(:last-of-type) {
        border-bottom: ${p.theme.borders.lightest};
      }
      svg {
        color: ${p.theme.colors.secondaryText};
      }
    `}
`;
